import Box from '@mui/material/Box';
import React from 'react';

import {
  publishOutcomeMessage,
  publishStatusDisplay,
} from './monitoring-publish-status-display';

import Button from '~/client/components/base/button';
import StatusPill from '~/client/components/base/status-pill';
import Text from '~/client/components/base/text';
import * as GQL from '~/graphql/generated/client';
import { packFieldWarningLines } from '~/shared/utils/pack-field-check';
import { useSnackbarContext } from '~c/components/base/snackbar';

function detailOf(publication: GQL.GuardianPublicationFieldsFragment): string {
  const parts: string[] = [];
  if (publication.createdAt) {
    parts.push(new Date(publication.createdAt).toLocaleString());
  }
  if (publication.bundleVersion) {
    parts.push(`version ${publication.bundleVersion}`);
  }
  if (publication.controlCount) {
    parts.push(`${publication.controlCount} controls`);
  }
  return parts.join(' · ');
}

export interface MonitoringPublishStatusProps {
  projectId: string;
}

const MonitoringPublishStatus: React.FC<MonitoringPublishStatusProps> = ({
  projectId,
}) => {
  const { data, error } = GQL.useGetGuardianPublicationsQuery({
    variables: { projectId, limit: 1 },
    skip: !projectId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: changesData, refetch: refetchChanges } =
    GQL.useGetGuardianUnpublishedChangesQuery({
      variables: { projectId },
      skip: !projectId,
      fetchPolicy: 'cache-and-network',
    });
  const Snackbar = useSnackbarContext();
  const [publish, { loading, error: publishError }] =
    GQL.usePublishGuardianScopeMutation({
      // By name, so the status line and the version history both refresh.
      refetchQueries: ['GetGuardianPublications'],
      onCompleted: ({ publishGuardianScope }) => {
        const outcome = publishOutcomeMessage(publishGuardianScope);
        Snackbar.open(outcome.text, { showOk: outcome.isProblem });
        refetchChanges();
      },
      onError: (err) =>
        Snackbar.open(`Could not publish: ${err.message}`, { showOk: true }),
    });

  // Publication history needs Witness management access; without it the panel stays hidden.
  if (error || !projectId) {
    return null;
  }

  const last = data?.guardianPublications?.[0];
  const display = publishStatusDisplay(
    last?.status,
    changesData?.guardianUnpublishedChanges
  );
  const showReason =
    last?.reason &&
    last.status !== GQL.GuardianPublicationStatus.Published &&
    last.status !== GQL.GuardianPublicationStatus.Unchanged;
  const ignoredFields = packFieldWarningLines(last?.fieldWarnings);

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <StatusPill tone={display.tone} label={display.label} />
        {last && (
          <Text sz="body14" color="secondary">
            {detailOf(last)}
          </Text>
        )}
        {showReason && (
          <Text sz="body14" color="secondary">
            {last?.stage
              ? `${last.stage.toLowerCase()}: ${last.reason}`
              : last?.reason}
          </Text>
        )}
        {publishError && (
          <Text sz="body14" color="error">
            {`Could not publish: ${publishError.message}`}
          </Text>
        )}
        <Box sx={{ ml: 'auto' }}>
          <Button
            outlined
            size="small"
            loading={loading}
            onClick={() => {
              if (!loading) {
                publish({ variables: { projectId } });
              }
            }}
          >
            {loading ? 'Publishing…' : 'Publish now'}
          </Button>
        </Box>
      </Box>
      {ignoredFields.length ? (
        <Box
          component="ul"
          sx={{ m: 0, mt: 1, pl: 2, maxHeight: 160, overflowY: 'auto' }}
        >
          {ignoredFields.map((line) => (
            <li key={line.key}>
              <Text sz="body14" color="secondary">
                {line.text}
              </Text>
            </li>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

export default MonitoringPublishStatus;
